import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Car, ArrowRightLeft, FileX, RefreshCw, Clock, Beaker } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

export default function Inmatriculari() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openModalId = searchParams.get('modal');

  const serviciiOptions = [
    {
      id: 'permanente',
      title: "Înmatriculări permanente",
      icon: Car,
      desc: "Înmatricularea autovehiculelor noi sau second-hand provenite din țară sau import.",
      docs: [
        'Cererea de înmatriculare',
        'Fișa de înmatriculare avizată de direcția taxe și impozite',
        'Cartea de Identitate a Vehiculului (CIV) original și copie',
        'Documentul care atestă dreptul de proprietate',
        'Polița RCA în termen de valabilitate',
        'Dovada plății contravalorii certificatului de înmatriculare'
      ]
    },
    {
      id: 'transcriere',
      title: "Transcrierea de proprietate",
      icon: ArrowRightLeft,
      desc: "Trecerea autovehiculului de pe un proprietar pe altul (vânzare-cumpărare în România).",
      docs: [
        'Cererea noului proprietar',
        'Contractul de înstrăinare-dobândire vizat de ANAF',
        'Cartea de Identitate a Vehiculului (CIV)',
        'Certificatul de înmatriculare al fostului proprietar (cu ITP valabil)',
        'Polița RCA pe numele noului proprietar',
        'Plăcuțele cu numărul de înmatriculare (dacă se dorește schimbarea sau e din alt județ)'
      ]
    },
    {
      id: 'temporare',
      title: "Înmatriculări temporare",
      icon: Clock,
      desc: "Înmatricularea temporară pentru vehicule achiziționate în leasing sau importate temporar.",
      docs: [
        'Cererea solicitantului',
        'Contractul de leasing / documentul vamal de import temporar',
        'Cartea de Identitate a Vehiculului (CIV)',
        'Polița RCA valabilă pentru perioada solicitată',
        'Taxele legale aferente'
      ]
    },
    {
      id: 'provizorii',
      title: "Înmatriculări provizorii",
      icon: Car,
      desc: "Eliberarea numerelor roșii, valabile maxim 90 de zile.",
      docs: [
        'Cererea pentru autorizare provizorie',
        'Copia documentului de proprietate',
        'Declarație privind vama (pentru vehicule din afara UE)',
        'Polița RCA valabilă',
        'Dovada plății taxei pentru numere provizorii'
      ]
    },
    {
      id: 'probe',
      title: "Înmatriculări pentru probe",
      icon: Beaker,
      desc: "Destinat dealerilor auto și fabricanților de vehicule.",
      docs: [
        'Cererea societății comerciale',
        'Certificat constatator de la Registrul Comerțului',
        'Documente care atestă calitatea de producător sau dealer',
        'Polița RCA',
        'Dovada plății contravalorii autorizației și a plăcuțelor'
      ]
    },
    {
      id: 'radieri',
      title: "Radieri autovehicule",
      icon: FileX,
      desc: "Radierea din circulație (dezmembrare, export, furt).",
      docs: [
        'Cererea de radiere',
        'Certificatul de înmatriculare',
        'Plăcuțele cu numărul de înmatriculare',
        'Documentul doveditor (certificat dezmembrare/declarație vamală/adeverință poliție)',
        'Cartea de Identitate a Vehiculului (CIV) original'
      ]
    },
    {
      id: 'preschimbari',
      title: "Preschimbări C.I.",
      icon: RefreshCw,
      desc: "Eliberarea unui nou certificat în caz de pierdere, deteriorare sau modificare date.",
      docs: [
        'Cererea solicitantului',
        'Cartea de Identitate a Vehiculului (CIV)',
        'Certificatul vechi (în caz de deteriorare sau modificare date)',
        'Actul care atestă modificarea datelor (dacă e cazul)',
        'Dovada plății contravalorii certificatului'
      ]
    }
  ];

  const handleOpenChange = (id, isOpen) => {
    if (isOpen) {
      setSearchParams({ modal: id }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <h1 className="text-3xl font-bold text-slate-800 border-b pb-4">Înmatriculări autovehicule</h1>
      
      <p className="text-slate-600 text-lg">
        Autovehiculele, din punct de vedere al proprietății sau al înmatriculării, trec prin diferite stări. Selectați tipul de procedură de mai jos pentru a afla documentele necesare:
      </p>

      <div className="grid md:grid-cols-2 gap-4 mt-8">
        {serviciiOptions.map((opt) => (
          <Dialog key={opt.id} open={openModalId === opt.id} onOpenChange={(isOpen) => handleOpenChange(opt.id, isOpen)}>
            <DialogTrigger asChild>
              <div className={`border rounded-lg p-5 shadow-sm transition-all cursor-pointer group text-left flex items-start gap-4 ${openModalId === opt.id ? 'bg-blue-50 border-blue-400' : 'bg-white hover:border-blue-400 hover:bg-blue-50/50'}`}>
                <div className={`p-3 rounded-lg transition-colors flex-shrink-0 ${openModalId === opt.id ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white'}`}>
                  <opt.icon className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-semibold text-blue-700 mb-1 group-hover:text-blue-800 text-lg">{opt.title}</h4>
                  <p className="text-sm text-slate-500">{opt.desc}</p>
                </div>
              </div>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-white">
              <DialogHeader>
                <DialogTitle className="text-xl text-blue-900">{opt.title}</DialogTitle>
                <DialogDescription className="text-slate-500 mt-2">
                  Documentele obligatorii necesare la ghișeu:
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                <ul className="space-y-3 m-0 list-none p-0">
                  {opt.docs.map((doc, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700 text-sm font-medium">{doc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </DialogContent>
          </Dialog>
        ))}
      </div>
    </div>
  );
}
